import { memo, useMemo } from 'react';
import { BaseEdge, getSmoothStepPath, Position } from '@xyflow/react';
import { MIN_SPAN_FOR_LABEL_OFFSET_PX } from '../../utils/flowEdgeUtils';

const DEFAULT_PATH_OFFSET = 20;
const DEFAULT_BORDER_RADIUS = 5;

/**
 * Shift the smoothstep bend point so parallel edges between the same nodes fan apart.
 * Horizontal handle pairs bend on a vertical segment (shift centerX).
 * Vertical handle pairs bend on a horizontal segment (shift centerY).
 */
export function getParallelCenterAdjustments({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  parallelOffset,
}) {
  if (!parallelOffset) return {};

  const defaultCenterX = (sourceX + targetX) / 2;
  const defaultCenterY = (sourceY + targetY) / 2;

  const isHorizontalFlow =
    (sourcePosition === Position.Left || sourcePosition === Position.Right) &&
    (targetPosition === Position.Left || targetPosition === Position.Right);

  if (isHorizontalFlow) {
    return { centerX: defaultCenterX + parallelOffset };
  }

  const isVerticalFlow =
    (sourcePosition === Position.Top || sourcePosition === Position.Bottom) &&
    (targetPosition === Position.Top || targetPosition === Position.Bottom);

  if (isVerticalFlow) {
    return { centerY: defaultCenterY + parallelOffset };
  }

  return { centerY: defaultCenterY + parallelOffset };
}

/**
 * Stagger labels on parallel edges when there is enough room along the edge span.
 */
export function getAdjustedLabelPosition({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  labelX,
  labelY,
  labelOffsetY,
  groupSize,
}) {
  if (!labelOffsetY || (groupSize ?? 1) <= 1) {
    return { labelX, labelY };
  }

  const spanX = Math.abs(targetX - sourceX);
  const spanY = Math.abs(targetY - sourceY);

  const isHorizontalFlow =
    (sourcePosition === Position.Left || sourcePosition === Position.Right) &&
    (targetPosition === Position.Left || targetPosition === Position.Right);

  if (isHorizontalFlow && spanX >= MIN_SPAN_FOR_LABEL_OFFSET_PX) {
    return { labelX, labelY: labelY + labelOffsetY };
  }

  const isVerticalFlow =
    (sourcePosition === Position.Top || sourcePosition === Position.Bottom) &&
    (targetPosition === Position.Top || targetPosition === Position.Bottom);

  if (isVerticalFlow && spanY >= MIN_SPAN_FOR_LABEL_OFFSET_PX) {
    return { labelX: labelX + labelOffsetY, labelY };
  }

  if (spanX >= MIN_SPAN_FOR_LABEL_OFFSET_PX && spanX >= spanY) {
    return { labelX, labelY: labelY + labelOffsetY };
  }

  if (spanY >= MIN_SPAN_FOR_LABEL_OFFSET_PX) {
    return { labelX: labelX + labelOffsetY, labelY };
  }

  return { labelX, labelY };
}

function WorkflowNavigationEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition = Position.Right,
  targetPosition = Position.Left,
  label,
  labelStyle,
  labelShowBg,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  style,
  markerEnd,
  markerStart,
  data,
  interactionWidth,
}) {
  const parallelOffset = data?.parallelOffset ?? 0;
  const labelOffsetY = data?.labelOffsetY ?? 0;
  const groupSize = data?.groupSize ?? 1;

  const { path, labelX, labelY } = useMemo(() => {
    const centerAdjustments = getParallelCenterAdjustments({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      parallelOffset,
    });

    const [edgePath, baseLabelX, baseLabelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      offset: DEFAULT_PATH_OFFSET,
      borderRadius: DEFAULT_BORDER_RADIUS,
      ...centerAdjustments,
    });

    const adjustedLabel = getAdjustedLabelPosition({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      labelX: baseLabelX,
      labelY: baseLabelY,
      labelOffsetY,
      groupSize,
    });

    return {
      path: edgePath,
      labelX: adjustedLabel.labelX,
      labelY: adjustedLabel.labelY,
    };
  }, [
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    parallelOffset,
    labelOffsetY,
    groupSize,
  ]);

  return (
    <BaseEdge
      id={id}
      path={path}
      labelX={labelX}
      labelY={labelY}
      label={label}
      labelStyle={labelStyle}
      labelShowBg={labelShowBg}
      labelBgStyle={labelBgStyle}
      labelBgPadding={labelBgPadding}
      labelBgBorderRadius={labelBgBorderRadius}
      style={style}
      markerEnd={markerEnd}
      markerStart={markerStart}
      interactionWidth={interactionWidth}
    />
  );
}

export default memo(WorkflowNavigationEdge);
